/**
 * EITL award decision panel.
 *
 * Renders one award proposal exactly as the backend states it: the
 * recommendation and why the cheaper loser lost, staleness (which blocks
 * APPROVE and only APPROVE), and the decision record. After any decision it
 * repeats the backend's own truth: nothing was awarded, no subcontract or PO
 * was issued, no external notification was sent — those are deferred actions.
 */
import { useEffect, useRef, useState } from "react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import {
  awardWorkflowApi,
  classifyWorkflowError,
} from "../../services/intelligenceService";
import { canDecide } from "../../lib/roles";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { UnknownValue } from "../../components/workflow/WorkflowStates";
import ExecutionAuthorizationPanel from "./ExecutionAuthorizationPanel";
import { fmtMoney, PROPOSAL_STATE_STYLES } from "./levelingUtils";
import {
  Trophy,
  ShieldAlert,
  CheckCircle2,
  XCircle,
  MessageCircleQuestion,
  FileEdit,
  Loader2,
  History,
  AlertTriangle,
  Lock,
} from "lucide-react";

const fmtStamp = (value) => {
  if (!value) return null;
  try {
    return format(parseISO(value), "MMM d, yyyy HH:mm");
  } catch {
    return String(value);
  }
};

const DECISIONS = [
  {
    decision: "APPROVE",
    label: "Approve",
    icon: CheckCircle2,
    variant: "default",
    needsNote: false,
  },
  {
    decision: "REQUEST_CLARIFICATION",
    label: "Request clarification",
    icon: MessageCircleQuestion,
    variant: "outline",
    needsNote: true,
  },
  {
    decision: "REQUEST_REVISION",
    label: "Request revision",
    icon: FileEdit,
    variant: "outline",
    needsNote: true,
  },
  {
    decision: "REJECT",
    label: "Reject",
    icon: XCircle,
    variant: "destructive",
    needsNote: true,
  },
];

const AwardDecisionPanel = ({ packageId, proposal, history, user, onDecided }) => {
  /**
   * DUPLICATE PREVENTION IS TWO REFS, NOT ONE FLAG.
   *
   * `disabled={submitting}` is React state, and React state is not a
   * synchronous mutex: two clicks inside one event-loop window both read the
   * pre-update value and both fire. One human decision would become two
   * recorded decisions on a commercial award.
   *
   *   decisionInFlight -- the per-request mutex. Set synchronously before the
   *     await, released when THIS request settles, whatever the outcome. A
   *     refusal (428/409/transport) must leave the human able to try again.
   *
   *   decidedRef -- the semantic latch. Set only once the server has actually
   *     recorded a decision for this proposal. It is deliberately NOT released
   *     in `finally`: after a success the dialog closes, but nothing stops a
   *     human reopening it and confirming again before the parent's reload
   *     lands, and that second confirm is a second consequence. The lifecycle,
   *     not the loading flag, is what closes the door.
   *
   * Both reset when the proposal identity changes -- a different proposal is a
   * different decision. Same shape as PreconCommitPanel's
   * commitInFlight/committedRef pair (#60) and OperatorPage's per-action
   * inFlight (#66).
   */
  const decisionInFlight = useRef(false);
  const decidedRef = useRef(false);
  useEffect(() => {
    decisionInFlight.current = false;
    decidedRef.current = false;
  }, [proposal?.proposalId]);

  const [pendingDecision, setPendingDecision] = useState(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [decisionResult, setDecisionResult] = useState(null);

  if (!proposal) return null;

  const rec = proposal.recommendation || {};
  const leveling = proposal.leveling || {};
  const staleness = proposal.staleness || {};
  const stale = Boolean(staleness.stale);
  const decidable = proposal.state === "PROPOSED";
  const userCanDecide = canDecide(user);

  const bidderOf = (quoteId) =>
    (leveling.quotes || []).find((q) => q.quoteId === quoteId) || null;
  const lowestSubmitted = bidderOf(leveling.lowestSubmittedQuoteId);
  const lowestRQ = bidderOf(leveling.lowestResponsiveQualifiedQuoteId);

  const submitDecision = async () => {
    if (!pendingDecision) return;
    const spec = DECISIONS.find((d) => d.decision === pendingDecision);
    if (spec?.needsNote && note.trim().length < 10) {
      toast.error("This decision requires a reason of at least 10 characters — it becomes the record.");
      return;
    }
    // Synchronous, before any await. See the refs above.
    if (decisionInFlight.current || decidedRef.current) return;
    decisionInFlight.current = true;
    setSubmitting(true);
    try {
      const result = await awardWorkflowApi.decide(packageId, {
        proposalId: proposal.proposalId,
        decision: pendingDecision,
        note: note.trim() || undefined,
      });
      // The server recorded it. This proposal is decided, and stays decided
      // even while the parent's reload is still in flight.
      decidedRef.current = true;
      setDecisionResult(result);
      setPendingDecision(null);
      setNote("");
      toast.success(result.message || `Decision recorded: ${pendingDecision}`);
      onDecided?.();
    } catch (err) {
      const c = classifyWorkflowError(err);
      if (c.kind === "stale") {
        toast.error("The proposal went stale before your decision was recorded. Re-run the recommendation.");
        onDecided?.();
      } else if (c.kind === "conflict") {
        toast.error(c.message || "This proposal was already decided.");
        onDecided?.();
      } else {
        toast.error(c.message);
      }
    } finally {
      // The MUTEX releases whatever happened; the LATCH does not. A refused
      // decision left the human able to decide, and must stay that way.
      decisionInFlight.current = false;
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Recommendation summary */}
      <Card className="border-emerald-500/30">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="w-4 h-4 text-emerald-400" />
              OMNΛS Recommendation
            </CardTitle>
            <div className="flex items-center gap-2">
              {proposal.confidence && (
                <Badge variant="outline" className="bg-muted/50 border-border font-normal">
                  Confidence: {proposal.confidence}
                </Badge>
              )}
              <Badge
                variant="outline"
                className={PROPOSAL_STATE_STYLES[proposal.state] || "bg-muted text-muted-foreground border-border"}
              >
                {proposal.state.replaceAll("_", " ")}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-6">
            <div>
              <p className="text-xs text-muted-foreground">Recommended provider</p>
              <p className="text-xl font-bold text-emerald-400">
                {proposal.recommendedBidderName || rec.recommendedBidder || (
                  <UnknownValue reason="The evaluation did not produce a recommendation." />
                )}
              </p>
              {rec.recommendedQuoteId && (
                <p className="text-[11px] text-muted-foreground">
                  Quote {rec.recommendedQuoteId}
                  {bidderOf(rec.recommendedQuoteId)?.revision
                    ? ` · revision ${bidderOf(rec.recommendedQuoteId).revision}`
                    : ""}
                </p>
              )}
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Evaluated amount</p>
              <p className="text-xl font-semibold">
                {fmtMoney(proposal.evaluatedAmount) ?? <UnknownValue />}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Lowest submitted</p>
              <p className="text-sm font-medium">
                {lowestSubmitted
                  ? `${lowestSubmitted.bidder} · ${fmtMoney(lowestSubmitted.submittedAmount)}`
                  : <UnknownValue />}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Lowest responsive & qualified</p>
              <p className="text-sm font-medium">
                {lowestRQ
                  ? `${lowestRQ.bidder} · ${fmtMoney(lowestRQ.normalizedAmount ?? lowestRQ.submittedAmount)}`
                  : <UnknownValue />}
              </p>
            </div>
          </div>

          {(rec.rationale || []).length > 0 && (
            <div className="rounded-lg bg-muted/30 p-3 space-y-1">
              <p className="text-xs font-semibold text-muted-foreground">Evaluation rationale</p>
              {rec.rationale.map((r, i) => (
                <p key={i} className="text-sm text-foreground/90">
                  {r}
                </p>
              ))}
            </div>
          )}

          {(rec.risks || []).length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-semibold text-muted-foreground">Qualification issues & risks</p>
              <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-0.5">
                {rec.risks.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </div>
          )}

          {(rec.scheduleImplications || []).length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-semibold text-muted-foreground">Schedule effects</p>
              <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-0.5">
                {rec.scheduleImplications.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </div>
          )}

          {(proposal.missingInformation || []).length > 0 && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-2.5 space-y-1">
              <p className="text-xs font-semibold text-amber-400 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" />
                Missing evidence
              </p>
              {proposal.missingInformation.map((m, i) => (
                <p key={i} className="text-xs text-muted-foreground">
                  {m}
                </p>
              ))}
            </div>
          )}

          <div className="text-[11px] text-muted-foreground flex flex-wrap gap-x-4 gap-y-0.5">
            <span>Evaluation date: {proposal.evaluationDate || "—"}</span>
            <span title="Identity of the commercial evidence this evaluation read.">
              Source fingerprint: {String(proposal.sourceFingerprint || "").slice(0, 12)}…
            </span>
            <span title="Identity of the evaluation inputs (weights, requirements, adjustments).">
              Evaluation fingerprint: {String(proposal.evaluationFingerprint || "").slice(0, 12)}…
            </span>
            <span>Version {proposal.versionNumber}</span>
          </div>
        </CardContent>
      </Card>

      {/* Staleness */}
      {stale && (
        <Card className="border-red-500/40 bg-red-500/5">
          <CardContent className="py-3 px-4 space-y-1">
            <p className="text-sm font-semibold text-red-400 flex items-center gap-1.5">
              <ShieldAlert className="w-4 h-4" />
              This proposal is stale — approval is disabled
            </p>
            <p className="text-xs text-muted-foreground">
              {staleness.sourceChanged &&
                "The commercial evidence (quotes) changed after this evaluation was calculated. "}
              {staleness.evaluationChanged &&
                "The evaluation inputs (weights, requirements, adjustments) changed after calculation. "}
              {(staleness.expiredSinceEvaluation || []).length > 0 &&
                "Quote validity or insurance expired since the evaluation date. "}
              Re-run the recommendation to evaluate against current facts. Reject / request
              revision / request clarification remain available.
            </p>
            {(staleness.reasons || []).map((r, i) => (
              <p key={i} className="text-xs text-red-300/80">
                {r}
              </p>
            ))}
            {(staleness.expiredSinceEvaluation || []).map((e, i) => (
              <p key={`e${i}`} className="text-xs text-red-300/80">
                {typeof e === "string" ? e : JSON.stringify(e)}
              </p>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Decision controls */}
      {decidable && (
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">EITL decision</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {!userCanDecide ? (
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5" />
                Award decisions require a project manager or admin role. You can review
                everything here, but the decision buttons are reserved.
              </p>
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  {DECISIONS.map(({ decision, label, icon: Icon, variant }) => {
                    const disabled = decision === "APPROVE" && stale;
                    return (
                      <Button
                        key={decision}
                        variant={variant}
                        size="sm"
                        disabled={disabled || submitting}
                        title={
                          disabled
                            ? "Approval is blocked while the proposal is stale. Re-run the recommendation first."
                            : undefined
                        }
                        onClick={() => setPendingDecision(decision)}
                      >
                        <Icon className="w-4 h-4 mr-1.5" />
                        {label}
                      </Button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Approving records your decision. It does not issue a subcontract, create a
                  purchase order, or notify anyone — those steps are separate and not enabled.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Execution authorization — the second, separate human decision */}
      <ExecutionAuthorizationPanel
        packageId={packageId}
        proposal={proposal}
        user={user}
        onAuthorized={onDecided}
      />

      {/* Post-decision truth */}
      {(decisionResult || (!decidable && proposal.decision)) && (
        <Card className="border-border bg-muted/20">
          <CardContent className="py-3 px-4 space-y-2">
            <p className="text-sm font-medium">
              Decision recorded: {(decisionResult?.decision || proposal.decision || "").replaceAll("_", " ")}
              {proposal.decidedAt && !decisionResult && (
                <span className="text-xs text-muted-foreground"> · {fmtStamp(proposal.decidedAt)}</span>
              )}
            </p>
            {proposal.decisionNote && (
              <p className="text-xs text-muted-foreground">Note: {proposal.decisionNote}</p>
            )}
            <div className="flex flex-wrap gap-1.5">
              <Badge variant="outline" className="bg-muted/50 border-border font-normal">No award issued</Badge>
              <Badge variant="outline" className="bg-muted/50 border-border font-normal">No subcontract</Badge>
              <Badge variant="outline" className="bg-muted/50 border-border font-normal">No purchase order</Badge>
              <Badge variant="outline" className="bg-muted/50 border-border font-normal">No external notification</Badge>
            </div>
            {(decisionResult?.deferredActions || []).length > 0 && (
              <div className="space-y-0.5">
                <p className="text-xs font-medium text-muted-foreground">Deferred actions</p>
                {decisionResult.deferredActions.map((d, i) => (
                  <p key={i} className="text-xs text-muted-foreground">
                    {typeof d === "string" ? d : d.description || d.target || JSON.stringify(d)}
                  </p>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Approval history */}
      {(history || []).length > 0 && (
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <History className="w-4 h-4 text-primary" />
              Proposal history
            </CardTitle>
          </CardHeader>
          <CardContent className="divide-y divide-border">
            {history.map((h) => (
              <div key={h.proposalId} className="py-2 flex items-center justify-between gap-2 text-sm">
                <div className="min-w-0">
                  <p className="font-medium truncate">
                    {h.recommendedBidderName || "No recommendation"}{" "}
                    <span className="text-muted-foreground font-normal">
                      {fmtMoney(h.evaluatedAmount) || ""}
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {fmtStamp(h.createdAt)}
                    {h.decidedAt ? ` · decided ${fmtStamp(h.decidedAt)}` : ""}
                  </p>
                </div>
                <Badge
                  variant="outline"
                  className={PROPOSAL_STATE_STYLES[h.state] || "bg-muted text-muted-foreground border-border"}
                >
                  {h.state.replaceAll("_", " ")}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Decision confirmation dialog */}
      <Dialog open={!!pendingDecision} onOpenChange={(open) => !open && setPendingDecision(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {DECISIONS.find((d) => d.decision === pendingDecision)?.label} —{" "}
              {proposal.recommendedBidderName || "proposal"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {pendingDecision === "APPROVE" ? (
              <p className="text-sm text-muted-foreground">
                You are recording an authorized human decision to accept this recommendation
                ({proposal.recommendedBidderName} · {fmtMoney(proposal.evaluatedAmount)}).
                Nothing is issued or sent as a result — commitment steps are separate.
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                A written reason (at least 10 characters) is required. It becomes part of the
                decision record.
              </p>
            )}
            <div>
              <Label className="text-xs">
                Reason / note{pendingDecision === "APPROVE" ? " (optional)" : " (required)"}
              </Label>
              <Textarea
                className="mt-1"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={
                  pendingDecision === "APPROVE"
                    ? "Optional context for the record"
                    : "Why — this is the record the bidder and the audit trail will see"
                }
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setPendingDecision(null)}>
                Cancel
              </Button>
              <Button size="sm" onClick={submitDecision} disabled={submitting}>
                {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
                Confirm decision
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AwardDecisionPanel;
